import { FormBody } from '@/containers/forms/FormBody'
import { FormRow } from '@/containers/forms/FormRow'
import { useAuth } from '@/context/AuthContext'
import { useOffersVehicleContext } from '@/context/OffersVehicleProvider'
import { useProcessDiscountRequest } from '@/hooks/useOffer'
import {
  Box, Button,
  Flex,
  FormControl, FormLabel,
  Grid,
  InputRightAddon,
  Text, Textarea,
  useToast
} from '@chakra-ui/react'
import type { DiscountRequest } from 'BrokerTsLib/src/dtos'
import { DiscountRequestState } from 'BrokerTsLib/src/enums/DiscountRequestState'
import * as global from 'brokertslib/src/helpers/global'
import { useForm } from 'BrokerTsLib/src/rhf'
import cloneDeep from 'lodash/cloneDeep'
import { useState } from 'react'
import { useInsuranceParams } from '../../context/InsuranceParamsProvider'
import ErrorSummary from '../ErrorSummary'
import { InputGroup } from '../InputGroup'

export const QuoteItemDiscountRequestForm = ({ offerId, requestData }: { offerId: number, requestData: DiscountRequest }) => {
  const { user } = useAuth()
  const toast = useToast()
  const [createNewRequest, setCreateNewRequest] = useState(false)
  const [isPostingForm, setIsPostingForm] = useState(false)

  const { recalculateOffers } = useOffersVehicleContext()
  const { mutateAsync: storeDiscountRequest } = useProcessDiscountRequest(offerId)


  const frmDefaultValues = cloneDeep(requestData)

  const {
    register,
    getValues,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm({ defaultValues: frmDefaultValues })

  const noRequestYet = requestData.state == DiscountRequestState.None

  const { isDisabled } = useInsuranceParams()

  const GetRequestStateDescription = (state: DiscountRequestState) => {
    let description: string | null = null
    switch (state) {
      case DiscountRequestState.Requested:
        description = 'Žádost o nadlimitní slevu byla odeslána'
        break
      case DiscountRequestState.Canceled:
        description = 'Žádost o nadlimitní slevu byla stornována'
        break
      case DiscountRequestState.Approved:
        description = 'Žádost o nadlimitní slevu byla schválena'
        break
      case DiscountRequestState.Declined:
        description = 'Žádost o nadlimitní slevu byla zamítnuta'
        break
    }

    return description
  }

  const handleChangeRequestedValue = (value: string, insuranceCategoryDiscountId: number, setNewValue: boolean) => {
    const discounts = getValues('discounts')
    const item = discounts!.find(x => x.requestedInsuranceCategoryDiscountId == insuranceCategoryDiscountId)!
    if (setNewValue) {
      item.newValue = parseInt(value)
    }
    else {
      item.requestedValue = parseInt(value)
    }
    setValue('discounts', discounts)
  }

  const sendRequest = (newState: DiscountRequestState) => {
    clearErrors()
    setIsPostingForm(true)
    const data = getValues()
    data.state = newState
    storeDiscountRequest(data).
      then((resp) => {
        const toastMsg = GetRequestStateDescription(newState)
        // const forceRecalculate = newState == DiscountRequestState.Approved
        // reloadDiscounts(forceRecalculate)
        recalculateOffers([offerId], newState == DiscountRequestState.Approved)
        if (toastMsg) {
          toast({
            title: toastMsg,
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
        }
      }).
      catch(err => {
        if (!(err.response && err.response.status === 400 && err.response.data)) return
        const data = err.response.data
        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join(' ') })
        })
      }).
      finally(() => setIsPostingForm(false))
  }

  const currentStateDescription = GetRequestStateDescription(requestData.state)

  const discountValues = watch('discounts')

  if ((user?.isSupervisor ?? false) && requestData.state != DiscountRequestState.Requested) {
    return null
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Text sx={{ fontWeight: 'bold', marginBottom: 4 }}>Nadlimitní slevy</Text>


      {noRequestYet && !createNewRequest &&
        <Button
          colorScheme={'primary'}
          onClick={() => setCreateNewRequest(true)}
        >Vytvořit novou žádost</Button>
      }

      {(!noRequestYet || createNewRequest) &&
        <FormBody>
          <FormControl isInvalid={!!errors.text?.message}>
            <FormLabel>
              Odůvodnění žádosti
            </FormLabel>
            <InputGroup
              as={Textarea}
              isDisabled={isDisabled || requestData.state != DiscountRequestState.None}
              errors={errors.text}
              {...register('text')}
            />
          </FormControl>

          <Grid
            sx={{
              height: '100%',
              gridTemplateColumns: { lg: 'repeat(2, max-content)' },
              columnGap: 8,
              rowGap: 4,
              placeItems: 'center',
            }}
          >
            {discountValues.map(disc => {
              return (
                <FormControl key={disc.requestedInsuranceCategoryDiscountId}>
                  <Flex sx={{ width: '100%', gap: 4, justifyContent: 'space-between' }}>
                    <FormLabel htmlFor={`disc_${disc.requestedInsuranceCategoryDiscountId}`}>
                      {disc.name}
                    </FormLabel>
                  </Flex>

                  <InputGroup
                    htmlSize={12}
                    type='number'
                    variant='outline'
                    name={`disc_${disc.requestedInsuranceCategoryDiscountId}`}
                    id={`disc_${disc.requestedInsuranceCategoryDiscountId}`}
                    sx={{ fontSize: 'md', fontWeight: 'bold' }}
                    value={disc.requestedValue != 0 ? disc.requestedValue.toString() : ''}
                    isDisabled={isDisabled || requestData.state != DiscountRequestState.None}
                    onChange={(e) => handleChangeRequestedValue(e.target.value, disc.requestedInsuranceCategoryDiscountId, false)}
                    rightElement={<InputRightAddon>{disc.unit}</InputRightAddon>}
                  >
                  </InputGroup>
                </FormControl>
              )
            }

            )}

          </Grid>

          {requestData.state == DiscountRequestState.Requested && requestData.userCanApprove &&
            <>
              <FormControl isInvalid={!!errors.answer?.message}>
                <FormLabel>
                  Odpověď
                </FormLabel>
                <InputGroup
                  as={Textarea}
                  isDisabled={isDisabled}
                  errors={errors.answer}
                  {...register('answer')}
                />
              </FormControl>

              <Grid
                sx={{
                  height: '100%',
                  gridTemplateColumns: { lg: 'repeat(2, max-content)' },
                  columnGap: 8,
                  rowGap: 4,
                  placeItems: 'center',
                }}
              >
                {discountValues.map(disc => {
                  return (
                    <FormControl key={disc.requestedInsuranceCategoryDiscountId}>
                      <Flex sx={{ width: '100%', gap: 4, justifyContent: 'space-between' }}>
                        <FormLabel htmlFor={`disc2_${disc.requestedInsuranceCategoryDiscountId}`}>
                          {disc.name}
                        </FormLabel>
                      </Flex>

                      <InputGroup
                        htmlSize={12}
                        type='number'
                        variant='outline'
                        name={`disc2_${disc.requestedInsuranceCategoryDiscountId}`}
                        id={`disc2_${disc.requestedInsuranceCategoryDiscountId}`}
                        sx={{ fontSize: 'md', fontWeight: 'bold' }}
                        value={disc.newValue != 0 ? disc.newValue.toString() : ''}
                        isDisabled={isDisabled}
                        onChange={(e) => handleChangeRequestedValue(e.target.value, disc.requestedInsuranceCategoryDiscountId, true)}
                        rightElement={<InputRightAddon>{disc.unit}</InputRightAddon>}
                      >
                      </InputGroup>
                    </FormControl>
                  )
                }

                )}

              </Grid>
            </>
          }

          {requestData.state != DiscountRequestState.None && !requestData.userCanApprove && currentStateDescription &&
            <Text sx={{ fontWeight: 'bold' }}>{currentStateDescription}</Text>
          }

          <ErrorSummary errors={errors} />

          {createNewRequest &&

            <Button
              colorScheme={'primary'}
              onClick={() => sendRequest(DiscountRequestState.Requested)}
              isLoading={isPostingForm}
            >Odeslat žádost</Button>
          }

          {requestData.state == DiscountRequestState.Requested && !requestData.userCanApprove &&

            <Button
              colorScheme={'primary'}
              onClick={() => sendRequest(DiscountRequestState.Canceled)}
              isLoading={isPostingForm}
            >Stornovat žádost</Button>
          }

          {requestData.state == DiscountRequestState.Requested && requestData.userCanApprove &&

            <FormRow>
              <Button
                colorScheme={'primary'}
                onClick={() => sendRequest(DiscountRequestState.Approved)}
                isLoading={isPostingForm}
              >Schválit v nové výši</Button>
              <Button
                colorScheme={'gray'}
                onClick={() => sendRequest(DiscountRequestState.Declined)}
                isLoading={isPostingForm}
              >Zamítnout</Button>
            </FormRow>
          }
        </FormBody>
      }

    </Box>
  )

}